import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { DIVISIONS } from "@/data/content";
import Navbar from "@/components/Navbar";
import EditorialMarquee from "@/components/EditorialMarquee";
import SolutionsGrid from "@/components/SolutionsGrid";
import UspGrid from "@/components/UspGrid";
import ProjectsTicker from "@/components/ProjectsTicker";
import LocationsSection from "@/components/LocationsSection";
import FaqSection from "@/components/FaqSection";
import AboutManifesto from "@/components/AboutManifesto";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer";

gsap.registerPlugin(ScrollTrigger);

export default function PortalView({ division, onHome }) {
  const d = DIVISIONS[division];
  const root = useRef(null);
  const [prefill, setPrefill] = useState("");

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".portal-hero-content > *",
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out", stagger: 0.12, delay: 0.2 }
      );
      gsap.utils.toArray("[data-reveal]").forEach((el) => {
        gsap.fromTo(
          el,
          { y: 48, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.55,
            ease: "power3.out",
            delay: parseFloat(el.dataset.delay || 0),
            scrollTrigger: { trigger: el, start: "top 92%" },
          }
        );
      });
      gsap.utils.toArray("[data-parallax-img]").forEach((img) => {
        gsap.fromTo(
          img,
          { yPercent: -9 },
          {
            yPercent: 9,
            ease: "none",
            scrollTrigger: {
              trigger: img.parentElement,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          }
        );
      });
    }, root);
    return () => ctx.revert();
  }, [division]);

  const jump = (id) => {
    if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -72 });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleEnquire = (s) => {
    setPrefill(
      `Hi Bigboss Digitech, I'm interested in ${s.title} (${d.label} division). Please share details and a quote.`
    );
    setTimeout(() => jump("contact"), 60);
  };

  return (
    <main ref={root} data-testid={`portal-${division}`}>
      <Navbar divisionLabel={d.label} onHome={onHome} />

      <header className="relative flex min-h-[72vh] items-end overflow-hidden sm:min-h-[560px]" data-testid="portal-hero">
        <div className="absolute inset-0 overflow-hidden">
          <img
            data-parallax-img
            src={d.heroImg}
            alt={d.heroAlt}
            className="absolute -top-[12%] left-0 h-[124%] w-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-[#0B0F19]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/40 to-[#0B0F19]/30" />
        <div className="portal-hero-content relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-32 sm:px-6 sm:pb-20 sm:pt-36 lg:px-8">
          <p className="kicker mb-5">{d.kicker}</p>
          <h1 className="max-w-3xl font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {d.heroLines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.4 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
            {d.heroTag}
          </p>
          <div className="mt-8 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:flex-wrap sm:mt-9 sm:gap-4">
            <button
              data-testid="hero-explore-solutions-btn"
              onClick={() => jump("solutions")}
              className="rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-hover hover:shadow-lg hover:shadow-brand/30"
            >
              Explore Solutions
            </button>
            <button
              data-testid="hero-contact-btn"
              onClick={() => jump("contact")}
              className="rounded-full border border-slate-500 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:border-brand hover:text-white"
            >
              Talk to an Expert
            </button>
          </div>
          <p className="mt-5 text-[11px] leading-relaxed tracking-wide text-slate-500">
            Free consultation · 24-year legacy · 15,000+ projects delivered
          </p>
        </div>
        <div className="absolute bottom-6 right-6 hidden animate-bounce text-slate-500 md:block">
          <ArrowDown size={20} />
        </div>
      </header>

      <EditorialMarquee />

      <SolutionsGrid division={d} onEnquire={handleEnquire} />
      <ProjectsTicker divisionKey={division} />
      <UspGrid />
      <AboutManifesto />
      <LocationsSection />
      <FaqSection />
      <ContactForm divisionLabel={d.label} prefill={prefill} />
      <Footer onHome={onHome} />
    </main>
  );
}
